import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import type { PreferencesTabId } from "./preferences-tabs"

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

import { AboutMePanel } from "./about-me-panel"
import { GeneralPanel } from "./general-panel"
import { MemoryPanelInline } from "./memory-panel-inline"
import { WhatWeKnowPanel } from "./what-we-know-panel"

export type { PreferencesTabId } from "./preferences-tabs"

export type PreferencesDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialTab?: PreferencesTabId
}

export function PreferencesDialog({ open, onOpenChange, initialTab }: PreferencesDialogProps) {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<PreferencesTabId>(initialTab ?? "general")

  useEffect(() => {
    if (open) {
      setActiveTab(initialTab ?? "general")
    }
  }, [open, initialTab])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] sm:max-w-3xl">
        <DialogHeader className="space-y-3 pb-2">
          <DialogTitle className="text-xl font-semibold">
            {t("personalization.preferences.title")}
          </DialogTitle>
        </DialogHeader>

        <Tabs
          className="flex min-h-0 flex-1 flex-col"
          value={activeTab}
          onValueChange={(v: string) => setActiveTab(v as PreferencesTabId)}
        >
          <TabsList className="w-full justify-start">
            <TabsTrigger data-testid="preferences-tab-general" value="general">
              {t("personalization.preferences.tabs.general")}
            </TabsTrigger>
            <TabsTrigger data-testid="preferences-tab-about-me" value="about-me">
              {t("personalization.preferences.tabs.aboutMe")}
            </TabsTrigger>
            <TabsTrigger data-testid="preferences-tab-memory" value="memory">
              {t("personalization.preferences.tabs.memory")}
            </TabsTrigger>
            <TabsTrigger data-testid="preferences-tab-what-we-know" value="what-we-know">
              {t("personalization.preferences.tabs.whatWeKnow")}
            </TabsTrigger>
          </TabsList>

          <DialogBody className="mt-2">
            <TabsContent value="general">
              <GeneralPanel />
            </TabsContent>
            <TabsContent value="about-me">
              <AboutMePanel
                active={open && activeTab === "about-me"}
                onClose={() => onOpenChange(false)}
              />
            </TabsContent>
            <TabsContent value="memory">
              <MemoryPanelInline active={open && activeTab === "memory"} />
            </TabsContent>
            <TabsContent value="what-we-know">
              <WhatWeKnowPanel
                active={open && activeTab === "what-we-know"}
                onSwitchTab={(tab) => setActiveTab(tab)}
              />
            </TabsContent>
          </DialogBody>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
